import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Switch, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { getLeadWebhook, setLeadWebhook, rotateLeadWebhook } from '@/data/client';


const LW_OVERRIDE_FIELDS: [string, string][] = [
  ['email', 'Email'], ['phone', 'Phone'], ['full_name', 'Full name'], ['first_name', 'First name'],
  ['last_name', 'Last name'], ['company', 'Company'], ['address', 'Address'], ['city', 'City'], ['state', 'State'], ['zip', 'Zip'],
];

// Direct Wufoo / Formspree lead capture for a client — a section in the settings
// modal. Shows the per-client webhook URLs + handshake, the AI grading prompt, and
// optional field overrides. Leads flow straight into leadIQ (no Zapier/BigQuery).
export function LeadCaptureSection({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['leadWebhook', orgId], queryFn: () => getLeadWebhook(orgId) });
  const [enabled, setEnabled] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [urls, setUrls] = useState<{ wufoo: string; formspree: string; nimbata: string }>({ wufoo: '', formspree: '', nimbata: '' });
  const [nimbata, setNimbata] = useState(false);
  const [callPrompt, setCallPrompt] = useState('');
  const [showSample, setShowSample] = useState(false);
  const [handshake, setHandshake] = useState('');
  const [showAdv, setShowAdv] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState('');
  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (!data || inited) return;
    setEnabled(data.enabled); setPrompt(data.gradePrompt); setOverrides(data.overrides || {});
    setUrls(data.urls); setHandshake(data.handshake); setInited(true);
    setNimbata(!!data.nimbata); setCallPrompt(data.callGradePrompt || '');
  }, [data, inited]);

  const copy = async (text: string, which: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(which); setTimeout(() => setCopied(''), 1500); } catch { /* ignore */ }
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await setLeadWebhook(orgId, { enabled, gradePrompt: prompt, overrides, nimbata, callGradePrompt: callPrompt });
      if (r.urls) setUrls(r.urls);
      /* Toggling capture changes the client's Connected badge and chip in the
         list behind this modal — without this it keeps the stale value until a
         reload, which reads as the setting not having saved. */
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] });
      setSaved(true); setTimeout(() => setSaved(false), 1600);
    } finally { setSaving(false); }
  };
  const rotate = async () => {
    if (!confirm('Rotate the webhook URL + handshake key? The old URLs stop working and you must update Wufoo/Formspree.')) return;
    setSaving(true);
    try { const r = await rotateLeadWebhook(orgId); setUrls(r.urls); setHandshake(r.handshake); } finally { setSaving(false); }
  };

  const urlRow = (label: string, value: string, key: string) => (
    <div className="lw-url">
      <span className="lw-url__label">{label}</span>
      <div className="lw-url__row">
        <code>{value || '—'}</code>
        <Button size="sm" variant="ghost" disabled={!value} onClick={() => copy(value, key)}>{copied === key ? 'Copied' : 'Copy'}</Button>
      </div>
    </div>
  );

  return (
    <section className="admin-sect">
      <h4 className="admin-sect__title">Direct lead capture (Wufoo / Formspree)</h4>
      <p className="dash-muted" style={{ margin: '0 0 10px' }}>
        Pull leads straight from a form webhook into leadIQ — no Zapier or BigQuery. Every lead lands as source <strong>Organic</strong>, channel <strong>Email</strong>, and is AI-graded on arrival.
      </p>
      <Switch label="Enable direct lead capture" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />

      <div className="lw-urls">
        {urlRow('Wufoo WebHook URL', urls.wufoo, 'wufoo')}
        {urlRow('Formspree webhook URL', urls.formspree, 'formspree')}
        {urlRow('Wufoo Handshake Key', handshake, 'hs')}
      </div>

      <p className="dash-muted lw-help">
        <strong>Wufoo:</strong> Form Settings → Integrations → WebHook → paste the URL, set the Handshake Key, and tick <em>Include Field and Form Structure</em>.<br />
        <strong>Formspree:</strong> add a Webhook to the form pointing at the Formspree URL. Point all of a client&rsquo;s forms at the same URL.
      </p>

      <Textarea label="Lead grading prompt" rows={6} value={prompt} onChange={(e) => setPrompt(e.target.value)}
        hint="Claude grades each lead with this. Keep the instruction to return JSON {score 1–5, analysis, tags}." />
      {data?.defaultPrompt && prompt !== data.defaultPrompt && (
        <button type="button" className="admin-linkbtn" onClick={() => setPrompt(data.defaultPrompt)}>Reset to default prompt</button>
      )}

      <button type="button" className="admin-linkbtn" style={{ marginTop: 8 }} onClick={() => setShowAdv((v) => !v)}>
        <Icon name={showAdv ? 'chevron-down' : 'chevron-right'} size={13} /> Field overrides (optional)
      </button>
      {showAdv && (
        <div className="lw-overrides">
          <p className="dash-muted">Fields auto-map by name/type. Override only when a form uses an unusual field name — enter the exact Wufoo field title or Formspree field name.</p>
          <div className="lw-overrides__grid">
            {LW_OVERRIDE_FIELDS.map(([k, label]) => (
              <Input key={k} label={label} placeholder="(auto)" value={overrides[k] || ''} onChange={(e) => setOverrides((o) => ({ ...o, [k]: e.target.value }))} />
            ))}
          </div>
        </div>
      )}

      <h4 className="admin-sect__title" style={{ marginTop: 22 }}>Nimbata call capture</h4>
      <p className="dash-muted" style={{ margin: '0 0 10px' }}>
        Calls post straight from Nimbata into leadIQ as channel <strong>Phone</strong>, with the caller, source, UTMs and click IDs, the recording link, and the transcript. The transcript is fetched a few minutes after the call (Nimbata transcribes after hangup) and the call is then AI-graded.
        While this is on, this client&rsquo;s <strong>call rows from BigQuery are skipped</strong> so nothing arrives twice; form leads keep syncing as before.
      </p>
      <Switch label="Take calls from the Nimbata webhook" checked={nimbata} onChange={(e) => setNimbata(e.target.checked)} />
      <div className="lw-urls">
        {urlRow('Nimbata webhook URL', urls.nimbata, 'nimbata')}
      </div>
      <p className="dash-muted lw-help">
        <strong>Nimbata</strong> (Agency plan): Integrations → Webhooks → add an outbound webhook with this URL, event <em>call ended</em> (fires once the outcome and duration are final). Include at least: Unique Call ID, Caller ID, Caller Name, Tracking Number, Destination Number, Configured Tracking Source, UTM Source / Medium / Campaign / Term, Google Ads Click ID, Landing Page, Start Time, Call Duration, Talk Duration, Call Outcome, Rec Link, Transcription. JSON or form-encoded both work; field names can be any spelling.
      </p>
      <Textarea label="Call grading prompt" rows={5} value={callPrompt} onChange={(e) => setCallPrompt(e.target.value)}
        hint="Grades each call from its transcript. Keep the instruction to return JSON {score 1–5, analysis, tags}." />
      {data?.defaultCallPrompt && callPrompt !== data.defaultCallPrompt && (
        <button type="button" className="admin-linkbtn" onClick={() => setCallPrompt(data.defaultCallPrompt)}>Reset to default call prompt</button>
      )}
      {data?.nimbataSample && (
        <>
          <button type="button" className="admin-linkbtn" style={{ marginTop: 8 }} onClick={() => setShowSample((v) => !v)}>
            <Icon name={showSample ? 'chevron-down' : 'chevron-right'} size={13} /> Last payload received{data.nimbataSampleAt ? ` · ${data.nimbataSampleAt.slice(0, 16).replace('T', ' ')} UTC` : ''}
          </button>
          {showSample && <pre className="gen-out__pre" style={{ maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{(() => { try { return JSON.stringify(JSON.parse(data.nimbataSample), null, 2); } catch { return data.nimbataSample; } })()}</pre>}
        </>
      )}

      <div className="lw-actions">
        <Button variant="ghost" size="sm" disabled={saving} onClick={rotate}>Rotate URL / key</Button>
        <Button variant="primary" size="sm" disabled={saving} onClick={save}>{saved ? 'Saved' : saving ? 'Saving…' : 'Save lead capture'}</Button>
      </div>
    </section>
  );
}
